/**
 * ResumeUpload — resume picker for the application forms.
 * Mirrors Flutter WebResume / WebEasyApplyResume + UploadFileTile:
 * browse for a file (no drag-and-drop, as in Flutter), see the uploaded file with download /
 * delete, shimmer while loading.
 * File checks (name, format, 5MB) are done by the caller so it can show Flutter's messages.
 */
import { useRef } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { FILE_ACCEPT_STRING } from '@/types/application.types';
import styles from './ResumeUpload.module.css';

interface ResumeUploadProps {
  resumeUrl: string;
  resumeUploaded: boolean;
  isLoading: boolean;
  isLoadingResumeData?: boolean;
  uploadProgress: number;
  onFileSelect: (file: File) => void;
  onDownload: () => void;
  onDelete: () => void;
  error?: string | null;
  /** Shows the "Upload Resume" heading (the application form has its own step title). */
  showTitle?: boolean;
}

/** The stored resume may be a URL — show just the file name. */
function displayFileName(resumeUrl: string): string {
  const name = decodeURIComponent(resumeUrl.split('?')[0].split('/').pop() || resumeUrl);
  return name.length > 48 ? `${name.slice(0, 40)}…${name.slice(-6)}` : name;
}

export function ResumeUpload({
  resumeUrl,
  resumeUploaded,
  isLoading,
  isLoadingResumeData,
  uploadProgress,
  onFileSelect,
  onDownload,
  onDelete,
  error,
  showTitle = true,
}: ResumeUploadProps) {
  const { t } = useLocalization();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pick = (file: File | undefined) => {
    if (file && !isLoading) onFileSelect(file);
  };

  return (
    <div className={styles.wrapper}>
      {showTitle && <h3 className={styles.sectionTitle}>{t('Upload Resume')}</h3>}

      <div className={styles.dropzone}>
        <div className={styles.dropIcon} aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11.5 2.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5l-4-4Z" />
            <path d="M11.5 2.5v4h4M10 14.5v-5M7.8 11.5 10 9.3l2.2 2.2" />
          </svg>
        </div>
        <button
          type="button"
          className={styles.browseBtn}
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
        >
          {t('Browse File')}
        </button>
        {/* Flutter WebUploadResume.acceptedResumeFormats + supportedFiles + 'less than' 5MB */}
        <p className={styles.dropHint}>
          {t('We accept ')}
          <strong>{t('DOC, DOCX, TXT, PDF, WPS')}</strong> {t('less than')} <strong>5MB</strong>
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept={FILE_ACCEPT_STRING}
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = '';
          }}
          hidden
        />
      </div>

      {isLoadingResumeData ? (
        <div className={styles.fileShimmer} aria-hidden="true" />
      ) : isLoading ? (
        <div className={styles.fileTile} aria-busy="true">
          <FileIcon />
          <div className={styles.fileText}>
            <div className={styles.progress}>
              <span style={{ width: `${Math.max(uploadProgress, 8)}%` }} />
            </div>
          </div>
        </div>
      ) : resumeUploaded && resumeUrl ? (
        <div className={styles.fileTile}>
          <FileIcon />
          <div className={styles.fileText}>
            <span className={styles.fileName} title={resumeUrl}>{displayFileName(resumeUrl)}</span>
            <span className={styles.fileMeta}>{t('Resume uploaded successfully.')}</span>
          </div>
          <button type="button" className={styles.iconBtn} onClick={onDownload} title={t('Download Resume')} aria-label={t('Download Resume')}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 3v10m0 0-4-4m4 4 4-4M4 16h12" />
            </svg>
          </button>
          <button type="button" className={`${styles.iconBtn} ${styles.deleteBtn}`} onClick={onDelete} title={t('Delete')} aria-label={t('Delete')}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3.5 5.5h13M8 5.5V4h4v1.5M5.5 5.5l.7 10.2a1 1 0 0 0 1 .8h5.6a1 1 0 0 0 1-.8l.7-10.2" />
            </svg>
          </button>
        </div>
      ) : null}

      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

function FileIcon() {
  return (
    <span className={styles.fileIcon} aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11.5 2.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5l-4-4Z" />
        <path d="M11.5 2.5v4h4M7.5 11h5M7.5 14h3.5" />
      </svg>
    </span>
  );
}
